/**
 * Arabic Translations for Al Ummah Now App
 *
 * Complete set of Arabic UI strings for the application.
 */

import type { AppTranslations } from '../types';

export const AR_TRANSLATIONS: AppTranslations = {
  common: {
    appName: 'الأمة الآن',
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجاح',
    cancel: 'إلغاء',
    save: 'حفظ',
    delete: 'حذف',
    edit: 'تعديل',
    create: 'إنشاء',
    search: 'بحث',
    filter: 'تصفية',
    sort: 'ترتيب',
    close: 'إغلاق',
    back: 'رجوع',
    backTo: 'العودة إلى {page}',
    next: 'التالي',
    previous: 'السابق',
    submit: 'إرسال',
    confirm: 'تأكيد',
    noResults: 'لا توجد نتائج',
    view: 'عرض',
    viewAll: 'عرض الكل',
    learnMore: 'اعرف المزيد',
    retry: 'إعادة المحاولة',
    demo: 'تجريبي',
    welcome: 'مرحباً',
    collapse: 'طي',
    expand: 'توسيع',
    loadMore: 'تحميل المزيد',
    saving: 'جاري الحفظ...',
    saved: 'تم الحفظ!',
    saveFailed: 'فشل الحفظ',
  },

  languages: {
    english: 'الإنجليزية',
    arabic: 'العربية',
  },

  header: {
    noChannelSelected: 'لم يتم اختيار قناة',
    switchAccount: 'تبديل الحساب',
    addAccount: 'إضافة حساب',
    logout: 'تسجيل الخروج',
    logoutAll: 'تسجيل الخروج من جميع الحسابات',
    settings: 'الإعدادات',
    darkMode: 'الوضع الداكن',
    lightMode: 'الوضع الفاتح',
    viewChannel: 'عرض القناة',
  },

  navigation: {
    navigationMenu: 'قائمة التنقل',
    navigation: 'التنقل',
    accounts: 'الحسابات',
    profile: 'الملف الشخصي',
    analytics: 'التحليلات',
    noChannel: 'لا توجد قناة',
    noActiveChannel: 'لا توجد قناة نشطة',
    createChannel: 'إنشاء قناة',
    channelName: 'اسم القناة',
    enterChannelName: 'أدخل اسم القناة',
    addAnotherAccount: 'إضافة حساب آخر',
    signOutOf: 'تسجيل الخروج من',
    signOutOfAll: 'تسجيل الخروج من جميع الحسابات',
    signIn: 'تسجيل الدخول',
    home: 'العودة إلى الرئيسية',
    light: 'فاتح',
    dark: 'داكن',
    channel: 'قناة',
    channels: 'قنوات',
  },

  channel: {
    myChannels: 'قنواتي',
    selectChannel: 'اختر قناة',
    createChannel: 'إنشاء قناة',
    editChannel: 'تعديل القناة',
    deleteChannel: 'حذف القناة',
    channelName: 'اسم القناة',
    channelDescription: 'وصف القناة',
    channelAvatar: 'صورة القناة',
    channelBanner: 'غلاف القناة',
    mainChannel: 'القناة الرئيسية',
    subscribers: 'المشتركون',
    videos: 'الفيديوهات',
    subscribe: 'اشترك',
    subscribed: 'مشترك',
    unsubscribe: 'إلغاء الاشتراك',
    manageChannels: 'إدارة القنوات',
    yourChannels: 'قنواتك',
    browseWithoutChannel: 'تصفح بدون قناة',
    enterChannelNamePlaceholder: 'أدخل اسم القناة',
    refreshChannelList: 'تحديث قائمة القنوات',
    singleChannel: 'قناة',
    clickToChangeCover: 'اضغط لتغيير الغلاف',
    clickToChangeAvatar: 'اضغط لتغيير الصورة',
    updateChannelInformation: 'تحديث معلومات قناتك',
    channelNotFoundMessage: 'القناة التي تحاول تعديلها غير موجودة.',
    charactersRemaining: 'حرف',
    saveChanges: 'حفظ التغييرات',
    channelNotFound: 'القناة غير موجودة',
    channelNameEnglish: 'اسم القناة (بالإنجليزية)',
    channelNameArabic: 'اسم القناة (بالعربية)',
    channelDescriptionEnglish: 'وصف القناة (بالإنجليزية)',
    channelDescriptionArabic: 'وصف القناة (بالعربية)',
    channelTrailer: 'إعلان القناة',
    noChannelTrailer: 'لا يوجد إعلان للقناة',
    about: 'حول القناة',
    content: 'المحتوى',
    channelSectionsPlaceholder: 'سيتم عرض أقسام القناة هنا',
    // About page
    description: 'الوصف',
    noDescription: 'لا يوجد وصف متاح',
    statistics: 'الإحصائيات',
    joined: 'تاريخ الانضمام',
    links: 'الروابط',
    twitter: 'تويتر',
    facebook: 'فيسبوك',
    instagram: 'إنستغرام',
    channelDetails: 'تفاصيل القناة',
    channelId: 'المعرف',
    category: 'الفئة',
    tags: 'الوسوم',
    // Channel Tabs
    home: 'الرئيسية',
    shorts: 'الفيديوهات القصيرة',
    live: 'البث المباشر',
    playlists: 'قوائم التشغيل',
    community: 'المجتمع',
    channels: 'القنوات',
    manageTabs: 'إدارة التبويبات',
    setAsDefault: 'تعيين كافتراضي',
    hideTab: 'إخفاء التبويب',
    showTab: 'إظهار التبويب',
    reorderTabs: 'إعادة ترتيب التبويبات',
    customTab: 'تبويب مخصص',
    addCustomTab: 'إضافة تبويب مخصص',
    tabName: 'اسم التبويب',
    tabContent: 'محتوى التبويب',
    tabs: {
      manage_tabs: 'إدارة تبويبات القناة',
      manage_tabs_description: 'قم بتخصيص التبويبات التي تظهر في قناتك وتعيين العرض الافتراضي',
      toggle_visibility: 'إظهار/إخفاء هذا التبويب',
      set_as_default: 'تعيين كتبويب افتراضي',
      cannot_hide_last_tab: 'لا يمكن إخفاء التبويب الأخير المرئي',
      cannot_hide_default_tab: 'لا يمكن إخفاء التبويب الافتراضي. يرجى تعيين تبويب آخر كافتراضي أولاً.',
      default_tab: 'التبويب الافتراضي',
      visible: 'مرئي',
      hidden: 'مخفي',
      save_changes: 'حفظ التغييرات',
      changes_saved: 'تم حفظ إعدادات التبويبات بنجاح',
      save_failed: 'فشل في حفظ إعدادات التبويبات',
      drag_to_reorder: 'اسحب لإعادة الترتيب',
      at_least_one_visible: 'يجب أن يكون تبويب واحد على الأقل مرئياً',
      default_must_be_visible: 'يجب أن يكون التبويب الافتراضي مرئياً',
    },
    // Channel Sections
    seeAll: 'عرض الكل',
    recentUploads: 'أحدث الفيديوهات',
    recentUploadsDescription: 'أحدث فيديوهاتك',
    popularUploads: 'الفيديوهات الشائعة',
    popularUploadsDescription: 'فيديوهاتك الأكثر مشاهدة',
    allPlaylists: 'جميع قوائم التشغيل',
    allPlaylistsDescription: 'جميع قوائم التشغيل العامة الخاصة بك',
    featuredVideos: 'الفيديوهات المميزة',
    featuredVideosDescription: 'فيديوهات مميزة من هذه القناة',
    likedVideos: 'الفيديوهات المعجب بها',
    latestLive: 'أحدث البث المباشر',
    featuredPlaylists: 'قوائم تشغيل مميزة',
    featuredPlaylistsDescription: 'قوائم تشغيل محددة تختارها',
    singlePlaylist: 'قائمة تشغيل واحدة',
    singlePlaylistDescription: 'قائمة تشغيل مميزة واحدة',
    playlistNotFound: 'قائمة التشغيل غير موجودة',
    noPlaylistsFound: 'لم يتم العثور على قوائم تشغيل',
    liveNow: 'مباشر الآن',
    liveNowDescription: 'البث المباشر الحالي',
    pastLiveStreams: 'البث المباشر السابق',
    pastLiveStreamsDescription: 'البث السابق',
    upcomingLiveStreams: 'قريباً',
    upcomingLiveStreamsDescription: 'البث المباشر المجدول',
    featuredChannels: 'قنوات مميزة',
    featuredChannelsDescription: 'القنوات التي توصي بها',
    subscriptionsSection: 'الاشتراكات',
    subscriptionsSectionDescription: 'القنوات المشترك بها',
    addSection: 'إضافة قسم',
    configureSection: 'تكوين القسم',
    addChannelSection: 'إضافة قسم للقناة',
    sectionTitle: 'عنوان القسم',
    enterSectionTitle: 'أدخل عنوان القسم',
    charactersCount: '{count}/100 حرف',
    playlistIds: 'معرفات قوائم التشغيل',
    enterOnePlaylistId: 'أدخل معرف قائمة تشغيل واحدة',
    enterPlaylistIds: 'أدخل معرفات قوائم التشغيل مفصولة بفواصل',
    enterExactlyOnePlaylistId: 'أدخل معرف قائمة تشغيل واحدة بالضبط',
    separatePlaylistIds: 'افصل معرفات قوائم التشغيل المتعددة بفواصل',
    channelIds: 'معرفات القنوات',
    enterChannelIds: 'أدخل معرفات القنوات مفصولة بفواصل',
    separateChannelIds: 'افصل معرفات القنوات المتعددة بفواصل',
    automaticContentMessage: 'سيعرض هذا النوع من الأقسام المحتوى تلقائياً من قناتك.\nلا حاجة لتكوين إضافي.',
    creating: 'جاري الإنشاء...',
    createSection: 'إنشاء القسم',
    automaticContent: 'محتوى تلقائي',
    playlist: 'قائمة تشغيل',
    aboutChannel: 'حول القناة',
    forYou: 'لك',
    additionalSettings: 'إعدادات إضافية',
    manageSections: 'إدارة الأقسام',
    customizeChannelLayout: 'تخصيص تخطيط قناتك',
    communitySettings: 'منشورات المجتمع',
    manageCommunityPosts: 'إدارة منشورات المجتمع',
    // Settings
    channelSettings: 'إعدادات القناة',
    generalSettings: 'الإعدادات العامة',
    editChannelDetails: 'تعديل اسم القناة والوصف والعلامة التجارية',
    backToChannel: 'العودة إلى القناة',
  },

  profile: {
    myProfile: 'ملفي الشخصي',
    createProfile: 'إنشاء ملف شخصي',
    editProfile: 'تعديل الملف الشخصي',
    displayName: 'الاسم المعروض',
    email: 'البريد الإلكتروني',
    bio: 'السيرة الذاتية',
    avatar: 'الصورة الشخصية',
    profileCreated: 'تم إنشاء الملف الشخصي بنجاح',
    profileUpdated: 'تم تحديث الملف الشخصي بنجاح',
    checkingProfile: 'جاري التحقق من الملف الشخصي...',
    redirectingToProfileCreation: 'إعادة توجيه إلى إنشاء الملف الشخصي...',
    redirecting: 'إعادة التوجيه...',
    welcomeMessage: 'مرحباً بك في الأمة الآن! لنقم بإعداد ملفك الشخصي للبدء.',
    welcomeUser: 'مرحباً {name}!',
    editProfileDescription: 'قم بتعديل معلومات ملفك الشخصي أدناه.',
    emailCannotBeChanged: 'لا يمكن تغيير عنوان بريدك الإلكتروني',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    yourFirstName: 'اسمك الأول',
    yourLastName: 'اسم عائلتك',
    firstNameArabic: 'الاسم الأول',
    lastNameArabic: 'اسم العائلة',
    displayNameOptional: 'كيف سيرى الآخرون اسمك (اختياري)',
    displayNameArabic: 'كيف سيرى الآخرون اسمك (اختياري)',
    displayNameEmpty: 'إذا تُرك فارغاً، سنستخدم اسمك الأول واسم عائلتك',
    bioOptional: 'أخبرنا عن نفسك (اختياري)',
    bioArabic: 'أخبرنا عن نفسك (اختياري)',
    privacyNotice: 'إشعار الخصوصية',
    privacyNoticeText: 'سيتم استخدام معلومات ملفك الشخصي لتخصيص تجربتك على الأمة الآن. يمكنك تحديث معلومات ملفك الشخصي في أي وقت في إعدادات الملف الشخصي.',
    welcome: 'مرحباً',
    editInstructions: 'اضغط على أيقونة التعديل لتحديث معلومات ملفك الشخصي',
    myPlaylists: 'قوائم التشغيل الخاصة بي',
    playlistsDescription: 'أنشئ وأدر قوائم تشغيل الفيديو الخاصة بك',
    newPlaylist: 'قائمة تشغيل جديدة',
    firstPlaylistMessage: 'أنشئ قائمة التشغيل الأولى لتنظيم مقاطع الفيديو المفضلة لديك',
    noPlaylistsAvailable: 'لا توجد قوائم تشغيل متاحة حاليًا',
    timezone: 'المنطقة الزمنية',
    timezoneDetected: 'المنطقة الزمنية المكتشفة',
    currentTimezone: 'المنطقة الزمنية الحالية',
  },

  video: {
    videos: 'الفيديوهات',
    uploadVideo: 'رفع فيديو',
    editVideo: 'تعديل الفيديو',
    deleteVideo: 'حذف الفيديو',
    videoTitle: 'عنوان الفيديو',
    videoDescription: 'وصف الفيديو',
    videoThumbnail: 'صورة مصغرة',
    videoDuration: 'المدة',
    videoViews: 'المشاهدات',
    videoLikes: 'الإعجابات',
    videoComments: 'التعليقات',
    videoShares: 'المشاركات',
    relatedVideos: 'فيديوهات ذات صلة',
    watchLater: 'مشاهدة لاحقاً',
    addToPlaylist: 'أضف إلى قائمة التشغيل',
    noVideoSelected: 'لم يتم اختيار فيديو',
    selectVideoMessage: 'اختر فيديو من القائمة للمشاهدة',
    // Video Chapters
    chapters: 'الفصول',
    chapter: 'فصل',
    noChapters: 'لا توجد فصول متاحة',
    currentChapter: 'الفصل الحالي',
    nextChapter: 'الفصل التالي',
    previousChapter: 'الفصل السابق',
    addChapter: 'إضافة فصل',
    editChapter: 'تعديل الفصل',
    deleteChapter: 'حذف الفصل',
    chapterTitle: 'عنوان الفصل',
    chapterTimestamp: 'التوقيت',
    parseFromDescription: 'تحليل من الوصف',
    showChapters: 'إظهار الفصول',
    hideChapters: 'إخفاء الفصول',
    chapterList: 'قائمة الفصول',
    parseChapters: 'تحليل الفصول',
    chapterParserHelp: 'الصيغ المدعومة: MM:SS أو HH:MM:SS متبوعة بعنوان الفصل',
    chapterParserExample: 'مثال: 0:00 المقدمة أو 1:23:45 الموضوع الرئيسي',
    chaptersDetected: 'فصول محددة',
    chaptersParsed: 'تم تحليل الفصول بنجاح',
    invalidChapterFormat: 'صيغة فصل غير صالحة',
    firstChapterMustBeZero: 'يجب أن يبدأ الفصل الأول عند 0:00',
    chapterTooClose: 'يجب أن تكون الفصول متباعدة بـ 10 ثوانٍ على الأقل',
    maxChaptersReached: 'الحد الأقصى 100 فصل مسموح',
    duplicateTimestamp: 'تم اكتشاف توقيت مكرر',
    chapterTitleRequired: 'عنوان الفصل مطلوب',
    chapterTitleMaxLength: 'يجب أن يكون عنوان الفصل 200 حرف أو أقل',
    moveChapterUp: 'نقل الفصل لأعلى',
    moveChapterDown: 'نقل الفصل لأسفل',
    previewChapters: 'معاينة الفصول',
    autoParseChapters: 'تحليل تلقائي للفصول',
    manualChapters: 'فصول يدوية',
    importedChapters: 'فصول مستوردة',
    // Channel videos page
    noVideosAvailable: 'لا توجد فيديوهات',
    latest: 'الأحدث',
    popular: 'الأكثر شعبية',
    oldest: 'الأقدم',
    views: 'مشاهدة',
    view: 'مشاهدة',
    // Date formatting
    today: 'اليوم',
    yesterday: 'أمس',
    daysAgo: 'منذ {count} أيام',
    weeksAgo: 'منذ {count} أسابيع',
    monthsAgo: 'منذ {count} أشهر',
    yearsAgo: 'منذ {count} سنوات',
    // Video Management Dashboard
    myVideos: 'فيديوهاتي',
    manageVideos: 'إدارة الفيديوهات',
    manageVideosDescription: 'إدارة فيديوهات قناتك',
    videoManagement: 'إدارة الفيديوهات',
    allVideos: 'جميع الفيديوهات',
    uploadedVideos: 'الفيديوهات المرفوعة',
    searchVideos: 'ابحث في الفيديوهات...',
    filterByStatus: 'تصفية حسب الحالة',
    filterByPrivacy: 'تصفية حسب الخصوصية',
    sortVideos: 'ترتيب الفيديوهات',
    allStatus: 'جميع الحالات',
    ready: 'جاهز',
    processing: 'جاري المعالجة',
    uploading: 'جاري الرفع',
    failed: 'فشل',
    public: 'عام',
    private: 'خاص',
    makePublic: 'اجعله عاماً',
    makePrivate: 'اجعله خاصاً',
    sortByDate: 'التاريخ',
    sortByViews: 'المشاهدات',
    sortByTitle: 'العنوان',
    bulkActions: 'إجراءات جماعية',
    selectAll: 'تحديد الكل',
    deselectAll: 'إلغاء تحديد الكل',
    deleteSelected: 'حذف المحدد',
    changePrivacy: 'تغيير الخصوصية',
    videosSelected: '{count} فيديو محدد',
    confirmDelete: 'هل أنت متأكد من حذف هذا الفيديو؟ لا يمكن التراجع عن هذا الإجراء.',
    confirmDeleteTitle: 'حذف الفيديو؟',
    confirmDeleteMultiple: 'هل أنت متأكد من حذف {count} فيديو؟ لا يمكن التراجع عن هذا الإجراء.',
    confirmDeleteMultipleTitle: 'حذف عدة فيديوهات؟',
    deleteSuccess: 'تم حذف الفيديو بنجاح',
    deleteMultipleSuccess: 'تم حذف {count} فيديو بنجاح',
    noVideosFound: 'لم يتم العثور على فيديوهات',
    uploadFirstVideo: 'ارفع أول فيديو للبدء',
    // Video Edit
    editVideoDetails: 'تعديل تفاصيل الفيديو',
    videoPreview: 'معاينة الفيديو',
    titleEnglish: 'العنوان (بالإنجليزية)',
    titleArabic: 'العنوان (بالعربية)',
    descriptionEnglish: 'الوصف (بالإنجليزية)',
    descriptionArabic: 'الوصف (بالعربية)',
    thumbnailSelector: 'الصورة المصغرة',
    autoGeneratedThumbnails: 'صور مصغرة تلقائية',
    customThumbnail: 'صورة مصغرة مخصصة',
    uploadThumbnail: 'رفع صورة مصغرة',
    selectThumbnail: 'اختر صورة مصغرة',
    dragDropThumbnail: 'اسحب وأفلت صورة، أو اضغط للتصفح',
    thumbnailRequirements: 'موصى به: 1280×720 (16:9)، الحد الأقصى 2 ميجابايت',
    setAsDefault: 'تعيين كافتراضي',
    currentThumbnail: 'الصورة المصغرة الحالية',
    videoTags: 'الوسوم',
    addTag: 'إضافة وسم',
    enterTag: 'أدخل وسماً',
    removeTag: 'إزالة الوسم',
    videoCategory: 'الفئة',
    selectCategory: 'اختر فئة',
    videoLanguage: 'اللغة',
    videoPrivacy: 'الخصوصية',
    publicVideo: 'عام - يمكن للجميع المشاهدة',
    privateVideo: 'خاص - يمكنك فقط المشاهدة',
    saveChanges: 'حفظ التغييرات',
    saving: 'جاري الحفظ...',
    discardChanges: 'تجاهل التغييرات',
    unsavedChanges: 'لديك تغييرات غير محفوظة',
    unsavedChangesMessage: 'هل أنت متأكد من المغادرة؟ ستفقد تغييراتك.',
    leaveWithoutSaving: 'غادر بدون حفظ',
    continueEditing: 'متابعة التعديل',
    updateSuccess: 'تم تحديث الفيديو بنجاح',
    updateFailed: 'فشل تحديث الفيديو',
    thumbnailUploadSuccess: 'تم رفع الصورة المصغرة بنجاح',
    thumbnailUploadFailed: 'فشل رفع الصورة المصغرة',
    // Video Player Controls
    play: 'تشغيل',
    pause: 'إيقاف مؤقت',
    mute: 'كتم الصوت',
    unmute: 'إلغاء كتم الصوت',
    fullscreen: 'ملء الشاشة',
    exitFullscreen: 'الخروج من ملء الشاشة',
    quality: 'الجودة',
    speed: 'السرعة',
    currentTime: 'الوقت الحالي',
    totalDuration: 'المدة الكاملة',
    // Video Duration Filters
    shortVideos: 'قصيرة (≤٣ دقائق)',
    standardVideos: 'عادية (٣-٢٠ دقيقة)',
    longVideos: 'طويلة (>٢٠ دقيقة)',
  },

  search: {
    searchPlaceholder: 'ابحث عن فيديوهات وقنوات والمزيد...',
    searchResults: 'نتائج البحث',
    searchVideos: 'الفيديوهات',
    searchChannels: 'القنوات',
    searchPlaylists: 'قوائم التشغيل',
    noSearchResults: 'لا توجد نتائج لبحثك',
    recentSearches: 'عمليات البحث الأخيرة',
    clearSearchHistory: 'مسح سجل البحث',
    trending: 'الشائع',
    searchPageTitle: 'بحث الفيديوهات',
    searchButton: 'بحث',
    searching: 'جاري البحث...',
    foundResults: 'تم العثور على {count} نتيجة',
    foundResultsFor: 'تم العثور على {count} نتيجة لـ "{query}"',
    activeFilters: 'المرشحات النشطة:',
    category: 'الفئة',
    language: 'اللغة',
    duration: 'المدة',
    dateFrom: 'من',
    dateTo: 'إلى',
    noResultsTitle: 'لم يتم العثور على نتائج',
    noResultsMessage: 'حاول تعديل مصطلحات البحث أو المرشحات',
    clearFilters: 'مسح المرشحات',
    filters: 'المرشحات',
    active: 'نشط',
    applyFilters: 'تطبيق المرشحات',
    clear: 'مسح',
    uploadDate: 'تاريخ الرفع',
    sortBy: 'ترتيب حسب',
    allCategories: 'جميع الفئات',
    islamicLectures: 'المحاضرات الإسلامية',
    quranRecitation: 'تلاوة القرآن',
    islamicHistory: 'التاريخ الإسلامي',
    prayerGuidance: 'إرشادات الصلاة',
    islamicStories: 'القصص الإسلامية',
    scholarlyDiscussions: 'المناقشات العلمية',
    allLanguages: 'جميع اللغات',
    english: 'الإنجليزية',
    arabic: 'العربية',
    anyDuration: 'أي مدة',
    short: 'قصير (أقل من 4 دقائق)',
    medium: 'متوسط (4-20 دقيقة)',
    long: 'طويل (أكثر من 20 دقيقة)',
    relevance: 'الصلة',
    uploadDateSort: 'تاريخ الرفع',
    viewCount: 'عدد المشاهدات',
    likes: 'الإعجابات',
    descending: 'تنازلي',
    ascending: 'تصاعدي',
    trendingVideos: 'الفيديوهات الرائجة',
    lastHour: 'الساعة الماضية',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    noTrendingVideos: 'لم يتم العثور على فيديوهات رائجة',
    checkBackLater: 'تحقق لاحقاً للحصول على المحتوى الرائج',
  },

  upload: {
    uploadTitle: 'رفع فيديو',
    uploadVideo: 'رفع فيديو',
    selectFile: 'اختر ملف',
    dragDropFiles: 'اسحب وأفلت الملفات هنا',
    videoDetails: 'تفاصيل الفيديو',
    uploading: 'جاري الرفع...',
    uploadComplete: 'اكتمل الرفع',
    uploadFailed: 'فشل الرفع',
    uploadProgress: 'تقدم الرفع',
    processingVideo: 'جاري معالجة الفيديو...',
    preparing: 'جاري التحضير للرفع...',
    makePublic: 'اجعل هذا الفيديو عاماً',
    cancelUpload: 'إلغاء الرفع',
    dropHere: 'أفلت الفيديو هنا',
    supports: 'يدعم',
    max: 'الحد الأقصى',
    shareDescription: 'شارك معرفتك وإلهامك مع الأمة',
    shareWithCommunity: 'شارك محتواك مع مجتمع الأمة الآن',
    videoUploadedMessage: 'تم رفع الفيديو بنجاح!',
    viewVideo: 'عرض الفيديو',
    uploadAnother: 'رفع فيديو آخر',
    waitingMessage: 'جاري معالجة الفيديو. قد يستغرق ذلك بضع دقائق...',
    percentComplete: '% مكتمل',
    guidelines: 'إرشادات الرفع',
    guidelineIslamicValues: 'يجب أن يتماشى المحتوى مع القيم والتعاليم الإسلامية',
    guidelineMaxSize: 'الحد الأقصى لحجم الملف: 5 جيجابايت',
    guidelineFormats: 'التنسيقات المدعومة: MP4، WebM، MOV',
    guidelineCopyright: 'تأكد من أن لديك الحق في رفع هذا المحتوى',
    guidelineProcessing: 'قد تستغرق المعالجة عدة دقائق حسب طول الفيديو',
    guidelineThumbnails: 'يمكن إضافة صور مصغرة مخصصة بعد الرفع',
    // TUS upload states
    paused: 'متوقف مؤقتاً',
    processing: 'جاري المعالجة...',
    complete: 'اكتمل الرفع',
    failed: 'فشل الرفع',
    pause: 'إيقاف مؤقت',
    resume: 'استئناف',
    cancel: 'إلغاء',
    retry: 'إعادة المحاولة',
    speed: 'سرعة الرفع',
    timeRemaining: 'الوقت المتبقي',
    bytesUploaded: 'تم رفعه',
  },

  batch: {
    batchUpload: 'الرفع الدفعي',
    selectFiles: 'اختر الملفات',
    selectedFiles: 'الملفات المحددة',
    filesSelected: '{count} ملف محدد',
    dragDropMultiple: 'اسحب وأفلت ملفات فيديو متعددة هنا (1-15 ملف)',
    clickToBrowse: 'أو اضغط للتصفح',
    fileLimit: '1-15 ملف فيديو (MP4، MOV، AVI، WebM)',
    removeFile: 'إزالة الملف',
    sharedMetadata: 'البيانات الوصفية المشتركة',
    individualMetadata: 'البيانات الوصفية الفردية',
    applyToAll: 'تطبيق على الكل',
    copyToAll: 'نسخ إلى الكل',
    resetToShared: 'إعادة تعيين إلى مشترك',
    autoNumbering: 'الترقيم التلقائي',
    enableAutoNumbering: 'تفعيل الترقيم التلقائي في العناوين',
    numberingPattern: 'النمط: استخدم {{n}} للرقم (مثل "الحلقة {{n}}")',
    uploadQueue: 'قائمة الانتظار للرفع',
    queueEmpty: 'لا توجد فيديوهات في قائمة الانتظار',
    addVideos: 'إضافة فيديوهات',
    startUploads: 'بدء الرفع',
    pauseAll: 'إيقاف الكل مؤقتاً',
    resumeAll: 'استئناف الكل',
    cancelAll: 'إلغاء الكل',
    retryFailed: 'إعادة محاولة الفاشل',
    overallProgress: 'التقدم الإجمالي',
    completed: 'مكتمل',
    pending: 'قيد الانتظار',
    uploading: 'جاري الرفع',
    paused: 'متوقف مؤقتاً',
    cancelled: 'ملغى',
    eta: 'الوقت المتوقع',
    overallEta: 'الوقت المتوقع الإجمالي',
    concurrency: 'التزامن',
    concurrentUploads: 'الرفع المتزامن',
    uploadSpeed: 'سرعة الرفع',
    totalProgress: 'التقدم الكلي',
    ofCompleted: '{completed} من {total} مكتمل',
    errors: {
      tooFewFiles: 'يرجى تحديد ملف فيديو واحد على الأقل',
      tooManyFiles: 'الحد الأقصى 15 ملفاً. لقد حددت {count} ملف.',
      invalidFileType: 'نوع ملف غير صالح: {filename}. يُسمح فقط بملفات الفيديو.',
      fileTooLarge: 'الملف كبير جداً: {filename} ({size})',
      initiateFailed: 'فشل في بدء الرفع الدفعي. يرجى المحاولة مرة أخرى.',
      uploadFailed: 'فشل الرفع لـ {filename}',
    },
  },

  schedule: {
    publishImmediately: 'نشر فوراً',
    scheduleLater: 'جدولة للنشر لاحقاً',
    selectDateTime: 'اختر التاريخ والوقت',
    timezone: 'المنطقة الزمنية',
    selectTimezone: 'اختر منطقتك الزمنية',
    scheduled: 'مجدول',
    publishNow: 'نشر الآن',
    cancelSchedule: 'إلغاء الجدولة',
    editSchedule: 'تعديل الجدولة',
    scheduledFor: 'مجدول لـ',
    scheduledVideos: 'الفيديوهات المجدولة',
    myScheduledVideos: 'فيديوهاتي المجدولة',
    noScheduledVideos: 'لا توجد فيديوهات مجدولة',
    noScheduledVideosMessage: 'ليس لديك أي فيديوهات مجدولة للنشر.',
    videoWillBePublished: 'سيتم نشر هذا الفيديو في',
    atTime: 'في الساعة',
    inYourTimezone: 'في منطقتك الزمنية',
    schedulingOptions: 'خيارات الجدولة',
    notifyOnPublish: 'إخطار المشتركين عند النشر',
    goesLiveIn: 'يبدأ البث المباشر خلال',
    dateAndTime: 'التاريخ والوقت',
    selectDate: 'اختر التاريخ',
    selectTime: 'اختر الوقت',
    minimumScheduleTime: 'يجب جدولة الفيديوهات قبل 5 دقائق على الأقل',
    maximumScheduleTime: 'لا يمكن جدولة الفيديوهات لأكثر من سنة واحدة مقدماً',
    invalidScheduleTime: 'وقت جدولة غير صالح',
    scheduleConflict: 'هذا الوقت محجوز بالفعل',
    scheduleSuccess: 'تم جدولة الفيديو بنجاح',
    scheduleFailed: 'فشل جدولة الفيديو',
    scheduleUpdated: 'تم تحديث الجدولة بنجاح',
    scheduleCancelled: 'تم إلغاء الجدولة بنجاح',
    publishingNow: 'جاري النشر الآن...',
    publishedSuccessfully: 'تم نشر الفيديو بنجاح',
    // Status labels
    statusScheduled: 'مجدول',
    statusPublishing: 'جاري النشر...',
    statusPublished: 'تم النشر',
    statusFailed: 'فشل',
    statusCancelled: 'ملغى',
    // Filter and sort
    filterByStatus: 'تصفية حسب الحالة',
    allStatuses: 'جميع الحالات',
    sortByDate: 'ترتيب حسب التاريخ',
    oldestFirst: 'الأقدم أولاً',
    newestFirst: 'الأحدث أولاً',
    // Actions
    viewVideo: 'عرض الفيديو',
    editVideo: 'تعديل الفيديو',
    deleteVideo: 'حذف الفيديو',
    // Time remaining
    timeRemaining: 'الوقت المتبقي',
    publishesIn: 'ينشر خلال',
    publishesSoon: 'ينشر قريباً',
    days: 'أيام',
    hours: 'ساعات',
    minutes: 'دقائق',
    seconds: 'ثوانٍ',
    // Empty states
    createFirstScheduledVideo: 'جدول فيديوك الأول لنشره تلقائياً',
    scheduleAVideo: 'جدولة فيديو',
    // Confirmations
    confirmCancelSchedule: 'هل أنت متأكد من إلغاء هذا النشر المجدول؟',
    confirmPublishNow: 'هل أنت متأكد من نشر هذا الفيديو الآن؟',
    // Preview
    schedulePreview: 'معاينة الجدولة',
    willBePublishedOn: 'سيتم نشره في',
    localTime: 'الوقت المحلي',
    utcTime: 'التوقيت العالمي',
    // Calendar View
    views: {
      list: 'قائمة',
      calendar: 'تقويم',
    },
    calendar: {
      month: 'شهر',
      week: 'أسبوع',
      day: 'يوم',
      today: 'اليوم',
      previous: 'السابق',
      next: 'التالي',
      calendarType: 'نوع التقويم',
      gregorian: 'ميلادي',
      hijri: 'هجري (إسلامي)',
      noEvents: 'لا توجد فيديوهات مجدولة لهذه الفترة',
      scheduledFor: 'مجدول لـ',
    },
    hijriMonths: {
      1: 'محرم',
      2: 'صفر',
      3: 'ربيع الأول',
      4: 'ربيع الثاني',
      5: 'جمادى الأولى',
      6: 'جمادى الثانية',
      7: 'رجب',
      8: 'شعبان',
      9: 'رمضان',
      10: 'شوال',
      11: 'ذو القعدة',
      12: 'ذو الحجة',
    },
  },

  analytics: {
    analyticsTitle: 'التحليلات',
    overview: 'نظرة عامة',
    views: 'المشاهدات',
    watchTime: 'وقت المشاهدة',
    subscribers: 'المشتركون',
    revenue: 'الإيرادات',
    topVideos: 'أفضل الفيديوهات',
    topCountries: 'أفضل البلدان',
    topDevices: 'أفضل الأجهزة',
    realtimeViews: 'المشاهدات المباشرة',
    last28Days: 'آخر 28 يوماً',
    last7Days: 'آخر 7 أيام',
    today: 'اليوم',
    demoMode: 'الوضع التجريبي',
    demoMessage: 'هذا عرض توضيحي للوحة التحليلات مع بيانات نموذجية',
    demoModeMessage: 'هذا عرض توضيحي للوحة التحليلات. يرجى تسجيل الدخول لعرض تحليلاتك الفعلية.',
    trackPerformance: 'تتبع أداء قناتك وتفاعل جمهورك',
  },

  time: {
    allTime: 'كل الأوقات',
    hours: 'ساعات',
    minutes: 'دقائق',
  },

  subscriptions: {
    subscriptionsTitle: 'الاشتراكات',
    mySubscriptions: 'اشتراكاتي',
    manage: 'إدارة',
    notificationSettings: 'إعدادات الإشعارات',
    subscriptionFeed: 'تغذية الاشتراكات',
    noSubscriptions: 'لا توجد اشتراكات بعد',
    loginToSeeVideos: 'سجل الدخول لمشاهدة أحدث الفيديوهات من القنوات المشترك بها.',
    latestVideos: 'أحدث الفيديوهات من القنوات المشترك بها',
    lastUpdated: 'آخر تحديث:',
    refresh: 'تحديث',
    loadingFeed: 'جاري تحميل تغذية الاشتراكات...',
    noVideosInFeed: 'لا توجد فيديوهات في التغذية',
    subscribeToSeeVideos: 'اشترك في القنوات لمشاهدة أحدث فيديوهاتهم هنا.',
    discoverChannels: 'اكتشف القنوات',
    loadMoreComingSoon: 'تحميل المزيد من الفيديوهات (قريباً)',
    showingVideosFrom: 'عرض {count} فيديو من اشتراكاتك.',
    wantMore: 'تريد المزيد؟ اشترك في المزيد من القنوات!',
  },

  feed: {
    feedTitle: 'التغذية',
    home: 'الرئيسية',
    trending: 'الشائع',
    subscriptions: 'الاشتراكات',
    history: 'السجل',
    watchLater: 'مشاهدة لاحقاً',
    likedVideos: 'الفيديوهات المعجب بها',
  },

  moderation: {
    moderationTitle: 'الإشراف',
    reports: 'البلاغات',
    reviewReport: 'مراجعة البلاغ',
    approve: 'قبول',
    reject: 'رفض',
    takeAction: 'اتخاذ إجراء',
    reportReason: 'سبب البلاغ',
    reportStatus: 'الحالة',
    reportedBy: 'أبلغ بواسطة',
    reportDate: 'تاريخ البلاغ',
    moderationDashboard: 'لوحة الإشراف',
    dashboardSubtitle: 'مراجعة واتخاذ إجراء بشأن المحتوى المبلغ عنه',
    totalReports: 'إجمالي البلاغات',
    pendingReports: 'البلاغات المعلقة',
    allTime: 'كل الأوقات',
    needsAttention: 'يحتاج إلى اهتمام',
    actions24h: 'الإجراءات (24 ساعة)',
    recentActivity: 'النشاط الأخير',
    avgResponseTime: 'متوسط وقت الاستجابة',
    meanResolution: 'متوسط الحل',
    filterReports: 'تصفية البلاغات',
    priority: 'الأولوية',
    contentType: 'نوع المحتوى',
    recentReports: 'البلاغات الأخيرة',
    reportsPendingAction: 'البلاغات المعلقة للإجراء',
    noReportsMatch: 'لا توجد بلاغات تطابق الفلاتر الحالية',
    accessDenied: 'تم رفض الوصول',
    backToDashboard: 'العودة إلى لوحة التحكم',
    reportDetails: 'تفاصيل البلاغ',
    reviewReportMessage: 'راجع البلاغ واتخذ الإجراء المناسب',
    reportReasonLabel: 'سبب البلاغ',
    descriptionLabel: 'الوصف',
    reportedByLabel: 'أبلغ بواسطة',
    reportDateLabel: 'تاريخ البلاغ',
    assignedToLabel: 'مُسند إلى',
    resolutionNotesLabel: 'ملاحظات الحل',
    reportedContentLabel: 'المحتوى المبلغ عنه',
    viewOriginalContent: 'عرض المحتوى الأصلي',
    chooseAction: 'اختر إجراءً',
    takeModerationAction: 'اتخاذ إجراء إشراف',
    alreadyResolved: 'تم حل هذا البلاغ بالفعل',
    actionType: 'نوع الإجراء',
    selectAction: 'اختر إجراءً',
    approveContent: 'قبول المحتوى',
    removeContent: 'إزالة المحتوى',
    hideContent: 'إخفاء المحتوى',
    warnUser: 'تحذير المستخدم',
    restrictUser: 'تقييد المستخدم',
    banUser: 'حظر المستخدم',
    dismissReport: 'رفض البلاغ',
    flagForReview: 'وضع علامة للمراجعة',
    briefReason: 'قدم سبباً موجزاً لإجراءك',
    notesOptional: 'ملاحظات (اختياري)',
    additionalNotes: 'أضف أي ملاحظات إضافية حول قرارك',
    takingAction: 'جاري اتخاذ الإجراء...',
    actionHistory: 'سجل الإجراءات',
    viewAllReports: 'عرض جميع البلاغات',
    userManagement: 'إدارة المستخدمين',
    reason: 'السبب',
    description: 'الوصف',
    assignedTo: 'مُسند إلى',
    status: {
      pending: 'معلق',
      inReview: 'قيد المراجعة',
      resolved: 'تم الحل',
      rejected: 'مرفوض',
      escalated: 'تم التصعيد',
      allStatuses: 'جميع الحالات',
      dismissed: 'مرفوض',
    },
    priorityEnum: {
      urgent: 'عاجل',
      high: 'عالية',
      medium: 'متوسطة',
      low: 'منخفضة',
      allPriorities: 'جميع الأولويات',
    },
    contentTypeEnum: {
      video: 'فيديو',
      comment: 'تعليق',
      channel: 'قناة',
      playlist: 'قائمة تشغيل',
      allTypes: 'جميع الأنواع',
    },
    reportReasonEnum: {
      inappropriateContent: 'محتوى غير لائق',
      spam: 'بريد مزعج',
      harassment: 'مضايقة',
      violence: 'عنف',
      hateSpeech: 'خطاب كراهية',
      falseInformation: 'معلومات خاطئة',
      copyright: 'انتهاك حقوق النشر',
      privacyViolation: 'انتهاك الخصوصية',
      other: 'أخرى',
    },
  },

  auth: {
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    signUp: 'إنشاء حساب',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetPassword: 'إعادة تعيين كلمة المرور',
    emailAddress: 'عنوان البريد الإلكتروني',
    password: 'كلمة المرور',
    confirmPassword: 'تأكيد كلمة المرور',
    rememberMe: 'تذكرني',
    loginSuccess: 'تم تسجيل الدخول بنجاح',
    logoutSuccess: 'تم تسجيل الخروج بنجاح',
    loginFailed: 'فشل تسجيل الدخول',
  },

  errors: {
    genericError: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
    networkError: 'خطأ في الشبكة. يرجى التحقق من اتصالك.',
    notFound: 'الصفحة غير موجودة',
    notFoundDescription: 'عذراً، الصفحة التي تبحث عنها غير موجودة. ربما تمت إزالتها أو أن الرابط غير صحيح.',
    unauthorized: 'غير مصرح. يرجى تسجيل الدخول.',
    forbidden: 'الوصول محظور',
    serverError: 'خطأ في الخادم. يرجى المحاولة لاحقاً.',
    validationError: 'خطأ في التحقق',
    uploadError: 'فشل الرفع. يرجى المحاولة مرة أخرى.',
    uploadFailed: 'فشل الرفع',
    sessionExpired: 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.',
    authRequired: 'المصادقة مطلوبة',
    channelRequired: 'لم يتم تحديد قناة نشطة. يرجى تحديد قناة أولاً.',
    failedToLoad: 'فشل في التحميل. يرجى المحاولة مرة أخرى.',
    failedToSave: 'فشل في الحفظ. يرجى المحاولة مرة أخرى.',
    failedToDelete: 'فشل في الحذف. يرجى المحاولة مرة أخرى.',
    failedToUpdate: 'فشل في التحديث. يرجى المحاولة مرة أخرى.',
    failedToCreate: 'فشل في الإنشاء. يرجى المحاولة مرة أخرى.',
    failedToSwitch: 'فشل في التبديل. يرجى المحاولة مرة أخرى.',
    failedToProcess: 'فشل في المعالجة. يرجى المحاولة مرة أخرى.',
    invalidFile: 'نوع ملف غير صالح.',
    fileTooLarge: 'الملف كبير جداً.',
    reportReasonRequired: 'يرجى تحديد سبب الإبلاغ عن هذا المحتوى.',
    loginRequired: 'يجب تسجيل الدخول لتنفيذ هذا الإجراء.',
  },

  success: {
    saved: 'تم الحفظ بنجاح',
    deleted: 'تم الحذف بنجاح',
    uploaded: 'تم الرفع بنجاح',
    updated: 'تم التحديث بنجاح',
    created: 'تم الإنشاء بنجاح',
  },

  validation: {
    required: 'هذا الحقل مطلوب',
    invalidEmail: 'عنوان بريد إلكتروني غير صالح',
    invalidUrl: 'عنوان URL غير صالح',
    minLength: 'الحد الأدنى للطول هو {min} حرفاً',
    maxLength: 'الحد الأقصى للطول هو {max} حرفاً',
    passwordMismatch: 'كلمات المرور غير متطابقة',
  },

  comments: {
    commentsCount: 'التعليقات ({count})',
    addComment: 'أضف تعليقاً...',
    cancel: 'إلغاء',
    comment: 'تعليق',
    commenting: 'جاري التعليق...',
    signInToComment: 'سجل الدخول للانضمام إلى المحادثة',
    signIn: 'تسجيل الدخول للتعليق',
    noComments: 'لا توجد تعليقات بعد. كن أول من يعلق!',
    liveUpdates: 'تحديث التعليقات في الوقت الفعلي',
    live: 'مباشر',
    offline: 'غير متصل',
  },

  community: {
    community: 'المجتمع',
    managePosts: 'إدارة المنشورات',
    newPost: 'منشور جديد',
    searchPosts: 'بحث في المنشورات...',
    allTypes: 'جميع الأنواع',
    text: 'نص',
    image: 'صورة',
    poll: 'استطلاع',
    quiz: 'اختبار',
    video: 'فيديو',
    published: 'منشور',
    scheduled: 'مجدول',
    drafts: 'مسودات',
    noResultsFound: 'لا توجد نتائج',
    noPostsYet: 'لا توجد منشورات',
    createFirstPost: 'إنشاء أول منشور',
  },

  notifications: {
    notificationCenter: 'مركز الإشعارات',
    new: 'جديد',
    view: 'عرض',
    markAsRead: 'تعليم كمقروء',
    markAllAsRead: 'تعليم الكل كمقروء',
    noNotifications: 'لا توجد إشعارات',
    viewAll: 'عرض الكل',
    testNotification: 'إشعار تجريبي',
    demoFeatures: 'ميزات تجريبية',
    likeNotification: 'إعجاب جديد',
    commentNotification: 'تعليق جديد',
    subscriptionNotification: 'مشترك جديد',
    uploadNotification: 'اكتمل الرفع',
    systemNotification: 'تحديث النظام',
    likeMessage: 'شخص ما أعجب بالفيديو الخاص بك "محاضرة إسلامية رائعة"',
    commentMessage: 'تعليق جديد على الفيديو الخاص بك: "ماشاء الله، مفيد جداً!"',
    subscriptionMessage: 'لديك مشترك جديد!',
    uploadMessage: 'تمت معالجة الفيديو "رفع جديد" بنجاح',
    systemMessage: 'مرحباً بك في الأمة الآن! ابدأ في استكشاف المحتوى الإسلامي.',
  },

  subtitle: {
    title: 'الترجمات',
    subtitles: 'الترجمات / التعليقات',
    subtitlesDescription: 'رفع ملفات ترجمة بلغات متعددة',
    upload: 'رفع',
    language: 'اللغة',
    default: 'افتراضي',
    delete: 'حذف',
    settings: 'الإعدادات',
    fontSize: 'حجم الخط',
    off: 'إيقاف',
    preview: 'معاينة الترجمة',
    showingFirst5: 'عرض أول 5 عناصر',
    warnings: 'مشاكل محتملة مكتشفة',
    moreCues: '+{count} عنصر إضافي',
    noIssues: 'لم يتم اكتشاف أي مشاكل. الترجمات جيدة!',
    uploadSubtitle: 'رفع ترجمة',
    selectLanguage: 'اختر اللغة',
    chooseLanguage: 'اختر لغة',
    selectFile: 'اختر ملف',
    subtitleFile: 'ملف الترجمة',
    uploadProgress: 'تقدم الرفع',
    uploading: 'جاري الرفع...',
    uploadSuccess: 'تم رفع الترجمة بنجاح!',
    uploadError: 'حدث خطأ أثناء الرفع',
    uploadFailed: 'فشل رفع الترجمة',
    setAsDefault: 'تعيين كمسار ترجمة افتراضي',
    setDefault: 'تعيين كافتراضي',
    fileSize: 'حجم الملف',
    status: 'الحالة',
    uploadedAt: 'تاريخ الرفع',
    actions: 'الإجراءات',
    noSubtitles: 'لم يتم رفع ترجمات بعد',
    noSubtitlesDescription: 'أضف مسارات ترجمة لجعل الفيديو الخاص بك متاحاً بلغات متعددة',
    uploadFirstSubtitle: 'رفع أول ترجمة',
    addSubtitle: 'إضافة ترجمة',
    validationErrors: 'أخطاء التحقق',
    validating: 'جاري التحقق من ملف الترجمة...',
    validationFailed: 'فشل التحقق من ملف الترجمة',
    completeAllFields: 'يرجى إكمال جميع الحقول',
    dragDropOrClick: 'اسحب وأفلت أو اضغط للتصفح',
    supportedFormats: 'المدعوم: SRT، VTT، SBV (الحد الأقصى 2 ميجابايت)',
    uploadFileArea: 'رفع ملف ترجمة',
    confirmDelete: 'حذف الترجمة؟',
    confirmDeleteDescription: 'هل أنت متأكد من حذف مسار الترجمة هذا؟ لا يمكن التراجع عن هذا الإجراء.',
    deleteSubtitle: 'حذف الترجمة',
    statusActive: 'نشط',
    statusProcessing: 'جاري المعالجة',
    statusFailed: 'فشل',
  },

  sections: {
    // Video Sections
    recentVideos: 'أحدث الفيديوهات',
    popularVideos: 'الفيديوهات الشائعة',
    trendingVideos: 'الرائج',
    recommendedVideos: 'موصى لك',
    noRecentVideos: 'لا توجد فيديوهات حديثة متاحة',
    noPopularVideos: 'لا توجد فيديوهات شائعة متاحة',
    noTrendingVideos: 'لا توجد فيديوهات رائجة متاحة',
    noRecommendedVideos: 'لا توجد فيديوهات موصى بها متاحة',
    // Time Periods
    periodDaily: 'اليوم',
    periodWeekly: 'هذا الأسبوع',
    periodMonthly: 'هذا الشهر',
    periodAll: 'كل الأوقات',
    // Strategies
    strategyPopular: 'شائع',
    strategyCategoryBased: 'مشابه',
    strategyRandom: 'اكتشف',
    basedOn: 'بناءً على',
    // Common
    loadMore: 'تحميل المزيد',
    collapse: 'طي',
    expand: 'توسيع',
  },
};
